import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import Select from 'react-select'
import { Modal } from 'react-bootstrap'

import { httpOk, GET, PUT, FILE, POST } from 'helpers/api'
import { notify } from 'helpers/helpers'
import useFileDrop from 'pages/parts/useFileDrop'
import ThemePicker, { defaultTheme } from 'pages/parts/ThemePicker'
import ChannelPosesMenu, { saveChannelPosesMenu } from 'pages/parts/ChannelPosesMenu'
import HintIcon from 'pages/parts/HintIcon'
import PageTabs from 'pages/parts/PageTabs'

/** Тема, повторяющая прежнее захардкоженное оформление мини-аппа */
const DEFAULT_THEME_CODE = 'yellow-light'

function normalizePoseStatus(value) {
	return value === true || value === 1 || value === '1' || value === 't' || value === 'true'
}

function mergePoses(savedPoses = [], helperPoses = []) {
	return helperPoses.map((helperPos) => {
		const saved = savedPoses.find((item) => Number(item.id) === Number(helperPos.id))

		if (!saved) {
			return {
				...helperPos,
				status: false,
				delivery_distance: helperPos.delivery_distance ?? '',
			}
		}

		return {
			...helperPos,
			...saved,
			status: normalizePoseStatus(saved.status),
			delivery_distance: saved.delivery_distance ?? helperPos.delivery_distance ?? '',
		}
	})
}

function mergeOrderTypes(savedOrderTypes = [], allOrderTypes = []) {
	const baseTypes = allOrderTypes.length > 0
		? allOrderTypes
		: [
			{ id: 1, name: 'Зал' },
			{ id: 2, name: 'С собой' },
			{ id: 3, name: 'Доставка' },
		]

	return baseTypes.map((type) => {
		const orderTypeId = Number(type.id)
		const saved = (savedOrderTypes ?? []).find((item) => (
			Number(item.order_type_id) === orderTypeId
		))

		return {
			...type,
			order_type_id: orderTypeId,
			order_type_sub_id: saved?.order_type_sub_id ?? null,
		}
	})
}

function Index() {
	const { t } = useTranslation()

	const [data, setData] = useState({
		'name': '',
		'description': '',
		'min_order_amount': '',
		'delivery_rule_id': '',
		'order_types': [],
		'poses': [],
	})
	const [activeTab, setActiveTab] = useState('general')
	const [modalFAQ, setModalFAQ] = useState(false)
	const [paymentTypes, setPaymentTypes] = useState([])
	const [deliveryRules, setDeliveryRules] = useState([])
	const [orderDeliveryTypes, setOrderDeliveryTypes] = useState([])
	const [botLanguages, setBotLanguages] = useState([])
	const [externalMenus, setExternalMenus] = useState([])
	const [themes, setThemes] = useState([])
	// null — ещё не загрузили, чтобы предупреждение не мигало при открытии
	const [mapConfigured, setMapConfigured] = useState(null)

	async function searchWithFilter() {
		const [botResponse, posesResponse, orderTypesResponse, categoriesResponse] = await Promise.all([
			GET('/chain/telegram-bot', {}, { loader: true }),
			GET('/helper/poses'),
			GET('/helper/order-types'),
			GET('/price-categories'),
		])

		if (httpOk(categoriesResponse)) setPriceCategories(categoriesResponse.data?.data ?? [])

		if (!httpOk(botResponse)) return

		const botData = Array.isArray(botResponse.data) ? {} : { ...(botResponse.data ?? {}) }
		const allOrderTypes = Array.isArray(orderTypesResponse?.data) ? orderTypesResponse.data : []

		if (httpOk(posesResponse)) {
			const savedPoses = Array.isArray(botData.poses) ? botData.poses : []
			const helperPoses = Array.isArray(posesResponse.data) ? posesResponse.data : []
			botData.poses = mergePoses(savedPoses, helperPoses)
		}

		botData.order_types = mergeOrderTypes(botData.order_types, allOrderTypes)
		setData(botData)
	}

	async function createUpdate(e) {
		e.preventDefault()

		const payload = {
			...data,
			order_types: (data.order_types ?? []).map((item) => ({
				order_type_id: item.order_type_id ?? item.id,
				order_type_sub_id: item.order_type_sub_id,
			})),
			poses: (data.poses ?? []).map((item) => ({
				...item,
				status: !!item.status,
			})),
		}

		var response;
		if (data.id) {
			response = await PUT('/chain/telegram-bot', payload, { loader: true })
		} else {
			response = await POST('/chain/telegram-bot', payload, { loader: true })
		}

		if (httpOk(response)) {
			await saveChannelPosesMenu(posMenus)
			searchWithFilter()
		}
	}

	function handleDataChange(e, type) {
		var dataCopy = { ...data }

		if (type === 'checkbox') {
			dataCopy[e.target.name] = e.target.checked
		}

		if (type === 'click') {
			dataCopy['start_video'] = ""
		}

		setData(dataCopy)
	}

	function handleChangePos(e, index) {
		const poses = [...(data.poses ?? [])]
		poses[index] = {
			...poses[index],
			status: e.target.checked,
		}
		setData({ ...data, poses })
	}

	function handleSelectOrderTypeSub(option, index) {
		var dataCopy = { ...data }
		const orderTypes = [...(dataCopy.order_types ?? [])]

		orderTypes[index] = {
			...orderTypes[index],
			order_type_sub_id: option.id,
		}
		dataCopy.order_types = orderTypes
		setData(dataCopy)
	}

	async function uploadVideoFile(file) {
		if (!file) return

		if (!file.type?.startsWith('video/')) {
			notify(t('video'))
			return
		}

		let formData = new FormData();
		formData.append("video", file);

		var response = await FILE('/upload/telegram-video', formData, {}, { loader: true });
		if (httpOk(response)) {
			var dataCopy = { ...data }
			dataCopy.settings.telegram_bot_start_video = response.data.url
			dataCopy.settings.telegram_bot_start_video_file_id = ""
			setData(dataCopy)
		}
	}

	async function fileUpload(e) {
		await uploadVideoFile(e.target.files[0])
	}

	const { dropProps, dropStyle } = useFileDrop(uploadVideoFile)

	async function getDeliveryRules() {
		const response = await GET('/helper/delivery-rules')
		if (httpOk(response)) setDeliveryRules(response.data)
	}

	async function getOrderDeliveryTypes() {
		const response = await GET('/helper/order-type-subs')
		if (httpOk(response)) setOrderDeliveryTypes(response.data)
	}

	async function getPaymentTypes() {
		const response = await GET('/helper/payment-types')
		if (httpOk(response)) setPaymentTypes(response.data)
	}

	async function getLanguages() {
		const response = await GET('/helper/languages')
		if (httpOk(response)) setBotLanguages(response.data)
	}

	async function getThemes() {
		const response = await GET('/helper/themes')
		if (httpOk(response)) setThemes(response.data ?? [])
	}

	async function getExternalMenus() {
		const response = await GET('/helper/external-menu')
		if (httpOk(response)) setExternalMenus(response.data ?? [])
	}

	// Мини-апп открывает карту выбора адреса только при ключе и типе карты,
	// иначе «Новый адрес» сразу берёт геолокацию клиента.
	async function getMapSettings() {
		const response = await GET('/chain/maps')
		if (httpOk(response)) setMapConfigured(!!(response.data?.token && response.data?.map_type_id))
	}

	function handleDefaultLanguageChange(option) {
		const selectedLanguages = data.languages ?? []
		const languages = option && !selectedLanguages.some(item => item.id === option.id)
			? [...selectedLanguages, option]
			: selectedLanguages

		setData({
			...data,
			'default_language_id': option ? option.id : null,
			'language': option ? option.app_code : null,
			'languages': languages,
		})
	}

	function handleLanguagesChange(options) {
		let selectedLanguages = options ?? []
		const defaultLanguage = botLanguages.find(option => option.id === data.default_language_id)

		if (defaultLanguage && !selectedLanguages.some(item => item.id === defaultLanguage.id)) {
			selectedLanguages = [...selectedLanguages, defaultLanguage]
		}

		setData({ ...data, 'languages': selectedLanguages })
	}

	// Тема обязательна, поэтому боту без неё подставляем ту, что мини-апп рисовал
	// раньше захардкоженной (yellow-light) — владелец меняет её при желании.
	useEffect(() => {
		if (!themes.length || data.theme_id) return

		const theme = themes.find(item => item.code === DEFAULT_THEME_CODE) ?? defaultTheme(themes)
		if (theme) setData(prev => ({ ...prev, 'theme_id': theme.id }))
	}, [themes, data.theme_id]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		searchWithFilter()
		getDeliveryRules()
		getPaymentTypes()
		getOrderDeliveryTypes()
		getLanguages()
		getExternalMenus()
		getThemes()
		getMapSettings()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const [priceCategories, setPriceCategories] = useState([])
	const [posMenus, setPosMenus] = useState({})

	const menuOptions = [
		{ id: null, name: t('main_menu') },
		...externalMenus,
	]
	const selectedMenuOption = data.external_menu_id
		? menuOptions.find(option => Number(option.id) === Number(data.external_menu_id))
		: menuOptions[0]

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">
						{t('telegram_bot')}
					</h4>
				</div>
				<div className="d-flex gap-2">
					<button className="btn btn-primary"
						onClick={() => setModalFAQ(true)}>
						<i className="uil uil-comment-alt-exclamation"></i>
					</button>
				</div>
			</div>

			{mapConfigured === false &&
				<div className="alert alert-warning d-flex align-items-center gap-2">
					<i className="uil uil-exclamation-triangle fz20"></i>
					<div>
						{t('telegram_bot_map_not_configured')}{' '}
						<Link to="/external-geocoding">{t('geocoding')}</Link>
					</div>
				</div>
			}

			<div className="card page-compact">
				<div className="card-body">
					<form onSubmit={(e) => createUpdate(e)} autoComplete="off">
						<PageTabs
							active={activeTab}
							onChange={setActiveTab}
							tabs={[
								{ key: 'general', label: t('general') },
								{ key: 'menu', label: t('menu') },
								{ key: 'orders', label: t('orders') },
								{ key: 'banners', label: t('banners') },
								{ key: 'poses', label: t('poses') },
								{ key: 'theme', label: t('theme') },
							]} />

						<div className={activeTab !== 'general' ? 'd-none' : 'row'}>
							<div className="col-lg-6">
								<div className="d-flex gap-2">
									<div className="form-group w-100">
										<label>{t('name2')}<span className="required-mark">*</span></label>
										<input type="text" className="form-control" name="name"
											value={data.name ?? ''}
											onChange={(e) => setData({ ...data, 'name': e.target.value })} />
									</div>
									<div className="form-group w-100">
										<label>{t("brand_name")}</label>
										<input type="text" className="form-control" name="brand_name"
											value={data.brand_name ?? ''}
											onChange={(e) => setData({ ...data, 'brand_name': e.target.value })} />
									</div>
								</div>
								<div className="form-group w-100">
									<label>URL</label>
									<input type="text" className="form-control" name="uuid"
										disabled
										value={`https://tg.mison.uz/${data.uuid}`}
										onChange={() => { }} />
								</div>
								<div className="d-flex gap-2">
									<div className="form-group w-100">
										<label>TELEGRAM TOKEN<span className="required-mark">*</span></label>
										<input type="text" className="form-control" name="telegram_bot_token"
											value={data.telegram_bot_token ?? ''}
											onChange={(e) => setData({ ...data, 'telegram_bot_token': e.target.value })} />
									</div>
									<div className="form-group w-100">
										<label>{t('bot_name')}</label>
										<input type="text" className="form-control" name="telegram_bot_username"
											disabled
											value={data.telegram_bot_username ? `@${data.telegram_bot_username}` : ''}
											onChange={() => { }} />
									</div>
								</div>
								<div className="form-group w-100">
									<label>{t('default_language')}<span className="required-mark">*</span></label>
									<Select
										options={botLanguages}
										value={
											botLanguages.find(option => option.id === data.default_language_id) ||
											botLanguages.find(option => option.app_code === data.language || option.code === data.language) ||
											''
										}
										onChange={(option) => handleDefaultLanguageChange(option)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => `${option.native_name} (${option.code})`}
										getOptionValue={(option) => option.id}
									/>
								</div>
								<div className="form-group w-100">
									<label>{t('client_languages')}<span className="required-mark">*</span></label>
									<Select
										isMulti
										options={botLanguages}
										value={data.languages ?? []}
										onChange={(options) => handleLanguagesChange(options)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => `${option.native_name} (${option.code})`}
										getOptionValue={(option) => option.id}
									/>
								</div>
							</div>

							<div className="col-lg-6">
								<div className="form-group w-100">
									<label>Текст при старте бота</label>
									<textarea className="form-control" name="start_text" rows="4"
										value={data.start_text ?? ''}
										onChange={(e) => setData({ ...data, 'start_text': e.target.value })} />
								</div>

								<div className="form-group">
									<label>Видео при старте бота</label>
									{data?.start_video &&
										<div className="d-flex gap-2">
											<input type="text" className="form-control" name="start_video"
												disabled
												value={data?.start_video ?? ''} />
											<div className="btn btn-danger"
												onClick={(e) => handleDataChange(e, 'click', 'start_video')}>
												<i className="uil uil-times"></i>
											</div>
										</div>
									}
									{!data?.start_video &&
										<div className="p-2 rounded"
											style={dropStyle ?? { border: '1px dashed var(--dark-border-strong, #ced4da)' }}
											{...dropProps}>
											<input type="file" className="form-control" name="start_video"
												placeholder=""
												onChange={(e) => fileUpload(e)} />
											<div className="text-muted fz14 mt-1">{t('drag_file_here')}</div>
										</div>
									}
								</div>

								<div className="d-flex justify-content-between mb-2">
									<div className="fw-500 vertical-center">
										ОТП код при регистрации
									</div>
									<div className="form-check form-switch form-switch-lg">
										<input type="checkbox" className="form-check-input"
											name="otp_code"
											checked={data.otp_code ? true : false}
											onChange={(e) => handleDataChange(e, 'checkbox')} />
									</div>
								</div>
								<div className="d-flex justify-content-between mb-2">
									<div className="fw-500 vertical-center">
										{t('access_internal_clients')}
									</div>
									<div className="form-check form-switch form-switch-lg">
										<input type="checkbox" className="form-check-input"
											name="is_inner_clients_only"
											checked={data.is_inner_clients_only ? true : false}
											onChange={(e) => handleDataChange(e, 'checkbox')} />
									</div>
								</div>
								<div className="d-flex justify-content-between mb-2">
									<div className="fw-500 vertical-center">
										{t('show_client_debt')}
									</div>
									<div className="form-check form-switch form-switch-lg">
										<input type="checkbox" className="form-check-input"
											name="is_client_debt_visible"
											checked={data.is_client_debt_visible ? true : false}
											onChange={(e) => handleDataChange(e, 'checkbox')} />
									</div>
								</div>
							</div>
						</div>

						<div className={activeTab !== 'menu' ? 'd-none' : 'row'}>
							<div className="col-lg-6">
								<div className="form-group w-100">
									<label>Ресторан загрузки цен<span className="required-mark">*</span></label>
									<Select
										options={data.poses}
										value={data.poses.find(option => option.id === data.pos_id) || ''}
										onChange={(option) => setData({ ...data, 'pos_id': option ? option.id : null })}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
								<div className="form-group w-100">
									<label>{t('external_menu')}</label>
									<Select
										options={menuOptions}
										value={selectedMenuOption || null}
										onChange={(option) => setData({ ...data, 'external_menu_id': option?.id ?? null })}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id ?? 'main'}
									/>
								</div>
								<div className="form-group w-100">
									<label>{t('price_category')}<HintIcon text={t('channel_price_category_hint')} /></label>
									<Select
										isClearable
										options={priceCategories}
										value={priceCategories.find(option => option.id === data.price_category_id) || ''}
										onChange={(option) => setData({ ...data, 'price_category_id': option ? option.id : null })}
										placeholder={t('base_price')}
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
							</div>
						</div>

						<div className={activeTab !== 'orders' ? 'd-none' : 'row'}>
							<div className="col-lg-6">
								<div className="form-group w-100">
									<label>{t('delivery_rules')}<span className="required-mark">*</span></label>
									<Select
										options={deliveryRules}
										value={deliveryRules.find(option => option.id === data.delivery_rule_id) || ''}
										onChange={(option) => setData({ ...data, 'delivery_rule_id': option ? option.id : null })}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>

								<label>{t('delivery_type')}<span className="required-mark">*</span></label>
								<div className="d-flex flex-column gap-2 mb-3">
									{data?.order_types?.map((item, index) => {
										const filteredOptions = orderDeliveryTypes.filter(
											option => Number(option.order_type_id) === Number(item.order_type_id)
										)
										const selectOptions = filteredOptions.length > 0
											? filteredOptions
											: orderDeliveryTypes

										return (
										<div key={item.order_type_id ?? index} className="d-flex justify-content-between">
											<p className="fw-600 vertical-center mb-0">{item.name}</p>
											<Select
												className="w-50"
												options={selectOptions}
												value={orderDeliveryTypes.find(option => option.id === item.order_type_sub_id) || null}
												onChange={(option) => handleSelectOrderTypeSub(option, index)}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
										)
									})}
								</div>

								<div className="form-group w-100">
									<label>{t('min_order_amount')}</label>
									<input type="number" className="form-control" name="min_order_amount"
										value={data.min_order_amount ?? ''}
										onChange={(e) => setData({ ...data, 'min_order_amount': e.target.value })} />
								</div>

								<div className="form-group w-100">
									<label>{t('payment_types')}</label>
									<Select
										isMulti
										options={paymentTypes}
										value={data.payment_types}
										onChange={(options) => setData({ ...data, 'payment_types': options })}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
							</div>

							<div className="col-lg-6">
								<div className="d-flex justify-content-between mb-2">
									<div className="fw-500 vertical-center">
										{t('tg_order_rating')}
									</div>
									<div className="form-check form-switch form-switch-lg">
										<input type="checkbox" className="form-check-input"
											name="telegram_rating"
											checked={data.telegram_rating ? true : false}
											onChange={(e) => handleDataChange(e, 'checkbox')} />
									</div>
								</div>
								{data.telegram_rating &&
									<div className="form-group">
										<label>{t('tg_rating_delay')}</label>
										<input type="number" className="form-control" name="telegram_rating_delay"
											min="0" max="1440"
											value={data.telegram_rating_delay ?? 0}
											onChange={(e) => setData({ ...data, 'telegram_rating_delay': e.target.value })} />
										<small className="text-muted">{t('tg_rating_delay_hint')}</small>
									</div>
								}
								<div className="d-flex justify-content-between mb-2">
									<div className="fw-500 vertical-center">
										{t('is_send_location')}
									</div>
									<div className="form-check form-switch form-switch-lg">
										<input type="checkbox" className="form-check-input"
											name="is_send_location"
											checked={data.is_send_location ? true : false}
											onChange={(e) => handleDataChange(e, 'checkbox')} />
									</div>
								</div>
							</div>
						</div>

						{/* Баннеры общие на бренд: грузятся один раз в «Бренд → Баннеры», бот отмечается галочкой */}
						<div className={activeTab !== 'banners' ? 'd-none' : ''}>
							<p className="mb-3">{t('brand_banners_moved')}</p>
							<Link to="/brand" className="btn btn-primary">{t('brand_banners_open')}</Link>
						</div>

						<div className={activeTab !== 'poses' ? 'd-none' : ''}>
							<ChannelPosesMenu
								poses={data?.poses ?? []}
								overrides={posMenus}
								setOverrides={setPosMenus}
								onToggle={handleChangePos} />
						</div>

						<div className={activeTab !== 'theme' ? 'd-none' : ''}>
							<ThemePicker
								title={t('theme')}
								required
								themes={themes}
								value={data.theme_id}
								onChange={theme => setData({ ...data, 'theme_id': theme?.id ?? null })}
							/>
						</div>

						<div className="btn-wrapper gap-2">
							<Link className="btn btn-warning btn-rounded btn-wide" to="/external-telegram-bot">
								{t('cancel')}
							</Link>
							<button type="submit" className="btn btn-primary btn-rounded btn-wide">
								{t('save')}
							</button>
						</div>
					</form>
				</div>
			</div>

			{/* MODAL FAQ */}
			<Modal show={modalFAQ} animation={false}
				centered size="lg"
				onHide={() => setModalFAQ(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('information')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div>
						<b>1 - Как создать бота</b>
						<div>1.1 - В телеграмме в поиске напишите <a href="https://t.me/Botfather" target="_blank" rel="noreferrer">Botfather</a></div>
						<div>1.2 - Напишите /newbot</div>
						<div>1.3 - Напишите названи бота "Ваш бренд"</div>
						<div>1.4 - Напишите ник бота пример mison_bot</div>
						<div>1.5 - Получите токен и вставьте его в раздел "Токен телеграм бота"</div>

						<hr />

						<b>2 - Контекстное меню</b>
						<div>2.1 - Напишите /mybots выберите бота » Bot Settings » Menu Button » Configure menu button</div>
						<div>2.2 - Отправьте ссылку {`https://tg.mison.uz/${data.uuid}`}</div>

						<hr />

						<b>3 - Контекстное меню</b>
						<div>3.1 - Напишите /mybots выберите » бота Bot Settings » Configure Mini App » Enable Mini App</div>
						<div>3.2 - Отправьте ссылку {`https://tg.mison.uz/${data.uuid}`}</div>

						<hr />

						<b>4 - Получать заказы в телеграм группу</b>
						<div>4.1 - Создайте приватную группу</div>
						<div>4.2 - Добавьте своего созданного бота в эту приватную группу</div>
						<div>4.3 - Скопируйте айди группы он будет минусовым значением пример <b>id чата -1002400598445</b></div>
						<div>4.4 - Вставьте его в <b>Telegram ID</b></div>

						<hr />

						<b>5 - Получать жалобы и предложение</b>
						<div>5.1 - Создайте приватную группу</div>
						<div>5.2 - Добавьте своего созданного бота в эту приватную группу</div>
						<div>5.3 - Скопируйте айди группы он будет минусовым значением пример <b>id чата -1002400598445</b></div>
						<div>5.4 - Вставьте его в разделе ID группы для жалоб</div>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL FAQ */}
		</>
	)
}

export default Index